import type { IntlShape } from 'react-intl'
import { getCheckInCheckOutText } from 'components/Search/utils'
import { INTL_DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isNonNullable, min } from 'lib/array/arrayUtils'
import { formatMomentInputAsIsoDate, intlDateFormatter } from 'lib/datetime/dateUtils'
import { isBedbankItemActive, isExperienceItemActive } from 'lib/order/isUpcoming'
import { isItemCancelled } from 'lib/order/orderUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import { formatFlightRouteLabel } from 'tripPlanner/components/Chat/flightDisplayUtils'
import { sumOccupancies } from 'tripPlanner/components/Chat/bookingOccupancyUtils'
import { formatOrderTileOccupancyLabel } from 'tripPlanner/components/Chat/orderTileDisplayUtils'

interface OrderTileCommon {
  orderId: string
  bookingItemIds: Array<string>
  flightBookingCount: number
  isHotelPlusFlights: boolean
  checkInDate?: string
  imageId?: string
  checkOutDate?: string
  occupancy?: App.Occupants
  primaryFlightItemId?: string
  flightDepartureAirport?: string
  flightArrivalAirport?: string
  flightDepartureDate?: string
}

/** Flights have no property name; callers title them from their route. */
type TileName =
  | { primaryType: 'flight', propertyName?: undefined }
  | { primaryType: 'hotel' | 'experience', propertyName: string }

export type OrderTileData = OrderTileCommon & TileName

interface BookingTileSourceCommon {
  itemId: string
  /** ISO date; undefined while unknown (no reservation yet, open-dated), which keeps the booking in the trip. */
  endDate?: string
  checkInDate?: string
  checkOutDate?: string
  imageId?: string
  occupancy?: App.Occupants
  flightItemId?: string
  flightDepartureAirport?: string
  flightArrivalAirport?: string
  flightDepartureDate?: string
}

type BookingTileSource = BookingTileSourceCommon & (
  | { bookingType: 'flight', propertyName?: undefined }
  | { bookingType: 'hotel' | 'experience', propertyName: string }
)

// Built from order-item fields, never fetched flight details, so a selected tag cannot change and orphan its booking.
function buildFlightBookingTag(
  intl: IntlShape,
  tile: Pick<OrderTileData, 'flightDepartureAirport' | 'flightArrivalAirport' | 'flightDepartureDate'>,
): string {
  const parts: Array<string> = [
    formatFlightRouteLabel(intl, tile.flightDepartureAirport, tile.flightArrivalAirport),
  ]

  if (tile.flightDepartureDate) {
    parts.push(intlDateFormatter(tile.flightDepartureDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale))
  }

  return parts.join(formatMiddleDotSeparator(intl))
}

export function buildBookingTag({
  intl,
  tile,
}: {
  intl: IntlShape
  tile: OrderTileData
}): string {
  if (tile.primaryType === 'flight') {
    return buildFlightBookingTag(intl, tile)
  }

  const parts: Array<string> = [tile.propertyName]

  if (tile.checkInDate && tile.checkOutDate) {
    parts.push(getCheckInCheckOutText(tile.checkInDate, tile.checkOutDate))
  } else if (tile.checkInDate) {
    parts.push(intlDateFormatter(tile.checkInDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale))
  }

  const occupancyLabel = formatOrderTileOccupancyLabel(intl, tile.occupancy)
  if (occupancyLabel) {
    parts.push(occupancyLabel)
  }

  return parts.join(formatMiddleDotSeparator(intl))
}

function getTileName(source: BookingTileSource): TileName {
  if (source.bookingType === 'flight') {
    return { primaryType: 'flight' }
  }
  return { primaryType: source.bookingType, propertyName: source.propertyName }
}

function getStaySources(order: App.Order): Array<BookingTileSource> {
  const hotelSources = order.items.flatMap((item): Array<BookingTileSource> => {
    if (isItemCancelled(item) || !item.offer?.property?.name) return []
    const { reservation } = item
    return [{
      bookingType: 'hotel',
      itemId: item.id,
      propertyName: item.offer.property.name,
      endDate: formatMomentInputAsIsoDate(reservation?.endDate),
      checkInDate: reservation?.startDate,
      checkOutDate: reservation?.endDate,
      imageId: item.offer.property.roomType?.images?.[0]?.id,
      occupancy: reservation && { adults: reservation.adults, children: reservation.children, infants: reservation.infants },
    }]
  })

  const bedbankSources = order.bedbankItems.flatMap((item): Array<BookingTileSource> => {
    if (!isBedbankItemActive(item) || !item.offer?.property?.name) return []
    const checkOutDate = item.checkOut.format(ISO_DATE_FORMAT)
    return [{
      bookingType: 'hotel',
      itemId: item.id,
      propertyName: item.offer.property.name,
      endDate: checkOutDate,
      checkInDate: item.checkIn.format(ISO_DATE_FORMAT),
      checkOutDate,
      imageId: item.offer.image?.id,
      occupancy: sumOccupancies(item.rooms.map(room => room.occupancy)),
    }]
  })

  return [...hotelSources, ...bedbankSources]
}

function getExperienceSources(order: App.Order): Array<BookingTileSource> {
  // Package inclusions are undated vouchers that end with their stay.
  const stayEndDates = new Map([
    ...order.items.map((item): [string, string | undefined] => [item.id, formatMomentInputAsIsoDate(item.reservation?.endDate)]),
    ...order.bedbankItems.map((item): [string, string] => [item.id, item.checkOut.format(ISO_DATE_FORMAT)]),
  ])
  return order.experienceItems.flatMap((item): Array<BookingTileSource> => {
    if (!isExperienceItemActive(item) || !item.title) return []
    const parentStayEndDate = item.accommodation_parent_item_id ? stayEndDates.get(item.accommodation_parent_item_id) : undefined
    return [{
      bookingType: 'experience',
      itemId: item.id,
      propertyName: item.title,
      endDate: formatMomentInputAsIsoDate(item.date) ?? parentStayEndDate,
      checkInDate: item.date,
    }]
  })
}

function getFlightSources(order: App.Order): Array<BookingTileSource> {
  if (!order.hasFlight) return []
  return order.flightItems.flatMap((item): Array<BookingTileSource> => {
    if (isItemCancelled(item)) return []
    return [{
      bookingType: 'flight',
      itemId: item.id,
      endDate: formatMomentInputAsIsoDate(item.bookedFlights?.at(-1)?.arrivalDate ?? item.arrivalDate ?? item.departureDate),
      flightItemId: item.id,
      flightDepartureAirport: item.departingAirport,
      flightArrivalAirport: item.arrivalAirport,
      flightDepartureDate: item.departureDate,
    }]
  })
}

// Undated bookings sort last so a dated one titles the trip.
function getStartTime(
  bookingType: OrderTileData['primaryType'],
  dates: Pick<OrderTileCommon, 'checkInDate' | 'flightDepartureDate'>,
): number {
  const startDate = bookingType === 'flight' ? dates.flightDepartureDate : dates.checkInDate
  return startDate ? new Date(startDate).getTime() : Infinity
}

function getSourceStartTime(source: BookingTileSource): number {
  return getStartTime(source.bookingType, source)
}

export function getOrderTileStartTime(tile: OrderTileData): number {
  return getStartTime(tile.primaryType, tile)
}

/**
 * The order's bookings that haven't ended by `today` (local ISO date, inclusive),
 * as one tile titled by its earliest stay, else experience, else flight.
 */
export function getOrderTile(order: App.Order, today: string): OrderTileData | undefined {
  const hasNotEnded = (source: BookingTileSource) => !source.endDate || source.endDate >= today
  const staySources = getStaySources(order).filter(hasNotEnded)
  const flightSources = getFlightSources(order).filter(hasNotEnded)
  const sourceGroups = [staySources, getExperienceSources(order).filter(hasNotEnded), flightSources]
  const primary = sourceGroups
    .map(sources => min(sources, getSourceStartTime))
    .find(isNonNullable)
  if (!primary) return undefined

  return {
    orderId: order.id,
    bookingItemIds: sourceGroups.flat().map(source => source.itemId),
    flightBookingCount: flightSources.length,
    isHotelPlusFlights: staySources.length > 0 && flightSources.length > 0,
    ...getTileName(primary),
    checkInDate: primary.checkInDate,
    checkOutDate: primary.checkOutDate,
    imageId: primary.imageId,
    occupancy: primary.occupancy,
    primaryFlightItemId: primary.flightItemId,
    flightDepartureAirport: primary.flightDepartureAirport,
    flightArrivalAirport: primary.flightArrivalAirport,
    flightDepartureDate: primary.flightDepartureDate,
  }
}
